<template>
  <v-col>
    <add-lessee-modal
      :changeLesseeInfo="changeLesseeInfo"
      :lesseeDialog="lesseeDialog"
      :toggleLesseeModal="toggleLesseeModal"
      :lesseeInfo="lesseeInfo"
    />
    <v-card>
      <v-card-title>
        <v-spacer/>
        <h1>Register Lease Agreement</h1>
        <v-spacer/>
      </v-card-title>
      <v-card-text>
        <v-sheet class="mt-5" height="70vh">
        <v-alert class="mb-5 mt-1" border="left" colored-border :type="'info'">
          <v-row>
            <v-col cols="8">
              Required fields are marked with a '*'.  
            </v-col>
            <v-col>
              <simple-fee-table :priceInfo="feesTable" />
            </v-col>
          </v-row>
        </v-alert>        
        <v-alert class="mb-2 mt-1" v-if="errorMessage" :type="'error'">
          {{ errorMessage }}
        </v-alert>
        <v-row>
          <v-col class="text-left">
            <h2>Leased Aussie *</h2>
            <v-row>
              <v-col cols="8">
                <v-text-field
                  hide-details
                  v-model="dogId"
                  label="Dog ID"
                  clearable
                  @click:clear="dogId = ''"
                />
              </v-col>
              <v-col>
              <v-btn color="primary" v-on:click="loadDogInfo">Find dog</v-btn>
              </v-col>
            </v-row>  
            <p class="mt-5" v-if="!!dogInfo">
              <b>Name: </b> {{ dogInfo.name }} <br />
              <b>ASCA ID: </b> {{ dogInfo.dogId }} <br />
              <b>Main Color: </b> {{ dogInfo.body }} <br />
              <b>DNA Status: </b> {{ dogInfo.dnaStatus }} <br />
              <v-divider></v-divider>
              <CoownersList :owners="dogInfo.owners" :ownershipType="dogOwnershipType" />
            </p>
          </v-col>
          <v-col class="ml-5">
            <div>
              <h2>
                Lease dates *
              </h2>
            <div width="300px">
            <datepicker 
                :date="startDate" 
                label="Lease Start Date" 
                v-on:updateDates="updateDates($event, 'startDate')"
              />&nbsp;
            <datepicker 
                :date="endDate" 
                label="Lease End Date" 
                v-on:updateDates="updateDates($event, 'endDate')"
              />&nbsp;
              </div>
              <admin-editable-coowners-list 
                :owners="lessees" 
                :ownershipType="leaseOwnershipType"
                v-on:updateSelectedPeople="updateSelectedPeople($event)"
              />
            </div>
          </v-col>
          <v-col class="ml-5">
              <h2> 
                Submission Date *
              </h2>              
                <datepicker 
                  :date="submissionDate"
                  label="Submission Date"
                  v-on:updateDates="updateDates($event, 'submissionDate')"
                />
              <h2 class="mt-5">
                Payment <span v-if="fee">${{fee}}</span>
              </h2>
              <v-row>
                <v-col>
                  <v-checkbox
                    color="primary"
                    label="Paid"
                    v-model="paymentPaid"
                  />
                </v-col>
                <v-col>
                  <v-select
                    v-if="paymentPaid"
                    :items="paymentTypes"
                    v-model="selectedPaymentType"
                    label="Payment type"
                    item-text="name"
                    item-value="id"
                  />
                </v-col>
              </v-row>
              <v-text-field
                hide-details
                v-model="paymentNotes"
                label="Payment notes"
                clearable
                @click:clear="paymentNotes = ''"
              />
              <h2 class="mt-5">
                Signatures captured
              </h2>          
              <div v-if="signatories && signatories[0]">
                  <span v-for="co in signatories" :key="co.personId">
                        <v-checkbox
                          color="primary"
                          :label="`${co.firstName} ${co.lastName} (Member: ${co.memberId}) `"
                          v-model="co.hasSigned"
                        >
                          <template v-slot:label>                         
                            {{ co.firstName }}
                            {{ co.lastName }}
                          </template>
                        </v-checkbox>
                  </span>
                </div>  
          </v-col>
        </v-row>
        </v-sheet>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn color="primary" v-on:click="submit" :disabled="!isValid">Register</v-btn>
      </v-card-actions>
    </v-card>
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import router from "@/router";

import FooterText from "@/components/common/FooterText.vue";
import SimpleFeeTable from "@/components/common/SimpleFeeTable.vue";
import AddLesseeModal from "@/components/common/AddLesseeModal.vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import AdminEditableCoownersList from "@/components/Admin/AdminEditableCoownersList.vue";

import adminService from '@/services/adminService';
import leaseService from '@/services/admin/leaseService';
import workflowService from "@/services/portal/workflowService";
import registerLeaseService from "@/services/portal/registerLeaseService";
import { getErrorMessage } from "@/services/errorHandler";

export default Vue.extend({
  components: { AddLesseeModal, SimpleFeeTable, FooterText, CoownersList, Datepicker, AdminEditableCoownersList },
  name: "AdminRegisterLease",
  data: function () {
    return {
        lesseeDialog: false,
        dogId: '',
        dogInfo: {
          owners: [] as any[]
        } as any,
        lesseeInfo: [] as any[],
        lessees: [] as any[],
        feesTable: {} as any,
        feesLoaded: false,
        primaryLessee: 0,
        paymentPaid: false,
        errorMessage: '',
        startDate: null as Temporal.PlainDate | null,
        endDate: null as Temporal.PlainDate | null,
        submissionDate: null as Temporal.PlainDate | null,
        signatories: [] as any[],
        paymentNotes: '',
        fee: null,
        paymentTypes: [] as any[],
        selectedPaymentType: null,
        pendingLeaseRegistrationsUrl: '/admin/registration/leases',   
    };
  },
  computed: {
    dogOwnershipType: function() {
      return OwnershipType.Dog;
    },
    leaseOwnershipType: function() {
      return OwnershipType.Lease;
    },    
    userInfo: function() {
      return this.$store.state.userInfo;
    },
    isValid: function() {
      return this.$data.dogId && this.$data.dogInfo.owners?.length && this.$data.startDate && this.$data.endDate && this.$data.submissionDate && this.$data.lessees && this.$data.lessees.length > 0 && this.$data.fee !== null && this.$data.feesLoaded && (!this.$data.paymentPaid || this.$data.selectedPaymentType !== null);
    }
  },
  created() {
    workflowService.getRegisterLeaseFees().then((res) => {
      this.feesTable = res;
      this.feesLoaded = true;
    }).catch((error) => {
      this.errorMessage = getErrorMessage(error);
    });
    adminService.getPaymentTypes().then((res) => {
      this.paymentTypes = res;
      this.selectedPaymentType = res[0];
    }).catch((error) => {
      this.paymentTypes = [];
      this.selectedPaymentType = null;
      this.errorMessage = getErrorMessage(error);
    });
  },
  watch: {
    primaryLessee: function(newValue) {
      leaseService.getLeaseFeeForPerson(newValue).then((res: any) => {
        this.fee = res.fee;
      }).catch((error) => {
        this.fee = null;
        this.errorMessage = getErrorMessage(error);
      });
    }
  },
  methods: {
    updateDates: function (val: any, key: string) {
      (this as any)[key] = val.date;
    },
    updateSelectedPeople: function (val: any) {
      this.lessees = val.newPeople;
    },
    async submit() {
        this.$store.dispatch("changeLoaderStatus",true);
        this.errorMessage = '';
        try {
          const primaryOwner = this.dogInfo.owners.filter((l: any) => l.isPrimary);
          const request = {
              requesterPersonId: primaryOwner[0].personId,
              dogId: this.dogId,
              startDate: this.startDate,
              endDate: this.endDate,
              submissionDate: this.submissionDate,
              lesees: this.lessees.map((l: any) => <object>{
                  personId: l.personId,
                  isPrimary: l.isPrimary,
              }),
              signatories: this.signatories,
              fee: this.fee,
              paymentInfo: this.paymentPaid ? {
                  paymentType: this.selectedPaymentType,
                  adminNotes: this.paymentNotes,
                  amount: this.fee,
                  isCaptured: this.paymentPaid
              } : {
                  paymentType: 1,
                  adminNotes: this.paymentNotes,
                  amount: this.fee,
                  isCaptured: this.paymentPaid
              }
          };
          const res = await leaseService.registerNewLease(request);
          if(!res.success){
            this.errorMessage = res.errorMessage;
          } else {            
            this.$store.dispatch("loadPersonTasks");
            this.$store.dispatch("loadAdminTasks", this.userInfo.personId);
            router.push({ path: this.pendingLeaseRegistrationsUrl });
          }
        } catch (error) {
          this.errorMessage = getErrorMessage(error);
        } finally {
          this.$store.dispatch("changeLoaderStatus",false);
        }
    },
    loadDogInfo(){
        registerLeaseService.getLeasedDog(this.dogId).then((res) => {
            if(!res){
              this.errorMessage = "Dog information reuquired for lease not found.";  
              this.dogInfo = {} as any;        
            } else {
              this.signatories = [] as any[];
              this.dogInfo = res;
              this.errorMessage ='';
              res.owners.map((l: any)=>{
                if(!this.signatories.find(s => s.personId == l.personId)){
                  this.signatories.push(<object>{
                      personId: l.personId,
                      firstName: l.firstName,
                      lastName: l.lastName,
                      hasSigned: false
                    })
                }
              });
              this.lessees.map((l: any)=>{
                if(!this.signatories.find(s => s.personId == l.personId)){
                  this.signatories.push(<object>{
                    personId: l.personId,
                    firstName: l.firstName,
                    lastName: l.lastName,
                    hasSigned: false
                  })
                }
              });
            }
        }).catch((e:any) => {
          this.errorMessage = "Dog information reuquired for lease not found.";  
          this.dogInfo = {} as any;   
        });
    },
    changePrimaryLessee(personId: number) {
      this.primaryLessee = personId;
      const index = this.lessees.map((c) => c.personId).indexOf(personId);
      this.lessees.forEach((co) => (co.isPrimary = false));
      this.lessees[index].isPrimary = true;
      this.lesseeInfo = this.lessees;
    },
    changeLesseeInfo: function(lessees: any[]) {
      if(lessees.length === 0){
        this.primaryLessee = 0;
      } else {
        if(this.primaryLessee === 0){
          this.primaryLessee = lessees[0].personId;
          lessees[0].isPrimary = true;
        }
      }
      //add missing lessees to the list of signatories
      lessees.map((l: any)=>{
        if(!this.signatories.find(s => s.personId == l.personId)){
          this.signatories.push(<object>{
              personId: l.personId,
              firstName: l.firstName,
              lastName: l.lastName,
              hasSigned: false
            })
        }
      });
      //remove removed lessees from the list of signatories, if they're not owners
      this.signatories.map((s: any)=>{
        if(this.lessees[0] && this.lessees.find(l => l.personId == s.personId) && this.dogInfo.owners[0] && !this.dogInfo.owners.find((o: any) => o.personId == s.personId)){
          this.signatories = this.signatories.filter(x => x.personId != s.personId);
        }
      });
      this.lessees = lessees;
      this.lesseeInfo = lessees;
    },
    toggleRules: function () {
      this.$data.rulesDialog = !this.$data.rulesDialog;
    },
    toggleLesseeModal: function () {
      this.$data.lesseeDialog = !this.$data.lesseeDialog;
    },
  },
});
</script>
